import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight, Check, ShieldCheck, Users, BarChart3, Sparkles, KeyRound,
  Settings2, Lock, Layers, Workflow, CreditCard, Building2, Headphones,
  Globe, FileSignature, UserPlus, Zap, Database,
} from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import networking from "@/assets/networking.jpg";
import heroCard from "@/assets/hero-card.webp";
import aiAbstract from "@/assets/ai-abstract.jpg";

export const Route = createFileRoute("/teams")({
  head: () => ({
    meta: [
      { title: "Teams — Digital Cards & AI CRM | Banana Cards" },
      { name: "description", content: "Equip every employee with a branded digital card, capture every lead in one CRM, and roll out NFC, SSO and analytics across your organisation." },
      { property: "og:title", content: "Teams — Digital Cards & AI CRM | Banana Cards" },
      { property: "og:description", content: "One platform for digital cards, lead capture, AI CRM, contracts and automations — built for sales teams, agencies and enterprises." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://banana.cards/teams" },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/teams" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "How is Teams different from the free plan?", acceptedAnswer: { "@type": "Answer", text: "Teams adds a shared workspace, brand-locked templates, bulk invite, SSO, role-based access, team analytics and priority support. Enterprise adds SCIM, audit logs, custom DPA and a dedicated CSM." } },
            { "@type": "Question", name: "Can we keep our existing CRM?", acceptedAnswer: { "@type": "Answer", text: "Yes. Banana plugs into Salesforce, HubSpot, Pipedrive, Attio and others via native integrations or webhooks. Use banana as the capture layer, your CRM as the system of record — or run banana standalone." } },
            { "@type": "Question", name: "Do you ship NFC cards globally?", acceptedAnswer: { "@type": "Answer", text: "Yes. We fulfil from EU and US hubs to 100+ countries, with re-orders managed centrally from your team dashboard." } },
            { "@type": "Question", name: "What happens when someone leaves the team?", acceptedAnswer: { "@type": "Answer", text: "One click off-boards the seat: the card stops sharing, contacts stay in the workspace, and ownership reassigns to a teammate. No data lost." } },
            { "@type": "Question", name: "Is banana GDPR & SOC 2 compliant?", acceptedAnswer: { "@type": "Answer", text: "Yes — SOC 2 Type II audited, GDPR-compliant, with an EU-only data residency option on enterprise plans." } },
          ],
        }),
      },
    ],
  }),
  component: TeamsPage,
});

const HERO_METRICS = [
  { v: "200k+", l: "professionals using banana" },
  { v: "65×", l: "faster follow-up vs. paper cards" },
  { v: "92%", l: "of teams save 4+ hours / week" },
  { v: "SOC 2", l: "Type II — enterprise ready" },
];

const TRUSTED_BY = ["Allianz", "JLL", "Northbeam", "Verta", "Pelo", "Linear", "HubSpot", "Notion"];

const PILLARS = [
  {
    icon: CreditCard,
    title: "On-brand digital cards for every seat",
    desc: "Roll out a consistent identity across the company — logo, colours, links and signatures locked to your brand. Update once, every card updates.",
    points: ["Brand-locked templates", "Bulk import via CSV / HRIS", "Email-signature generator"],
  },
  {
    icon: Users,
    title: "Every lead lands in one shared CRM",
    desc: "When a teammate taps, scans or shares, the contact lands in a shared workspace — enriched, deduped, and assigned to the right pipeline.",
    points: ["Shared & private pipelines", "AI lead enrichment", "Round-robin assignment"],
  },
  {
    icon: ShieldCheck,
    title: "Security & control built for IT",
    desc: "SSO, SCIM provisioning, role-based access, audit logs and granular permissions — data stays yours, even when people leave.",
    points: ["SAML SSO + SCIM", "Granular roles & RBAC", "GDPR & SOC 2 Type II"],
  },
];

const DEEP_DIVES = [
  {
    eyebrow: "Onboarding",
    title: "Equip the whole team",
    italic: "in a single afternoon.",
    desc: "Invite via CSV, Google Workspace or your HRIS. Banana auto-generates a branded card for every employee, ships their NFC product to their desk, and keeps profiles in sync as people change roles.",
    image: networking,
    points: [
      "Bulk invite & auto-card generation",
      "Google Workspace, Okta, BambooHR sync",
      "Off-board in one click — keeps the data, deactivates the seat",
      "Centralised NFC fulfilment & re-orders",
    ],
  },
  {
    eyebrow: "Lead capture",
    title: "Turn every meeting",
    italic: "into pipeline.",
    desc: "Tap to share, scan paper cards, forward emails or drop a voice note — banana's AI captures the contact, enriches it with firmographics, and drafts a follow-up in your tone of voice.",
    image: aiAbstract,
    reverse: true,
    points: [
      "AI card-scanner & email parser",
      "Firmographic + social enrichment",
      "Auto-drafted follow-ups in your voice",
      "Smart routing to the right rep",
    ],
  },
  {
    eyebrow: "Analytics",
    title: "See what's working —",
    italic: "across every rep.",
    desc: "Real-time dashboards for taps, captures, meetings booked and revenue influenced. Filter by team, region or campaign to see who's generating pipeline and where to double down.",
    image: heroCard,
    points: [
      "Per-rep & per-team leaderboards",
      "Conversion funnel & cohort views",
      "UTM-aware attribution",
      "Export to BI tools or Sheets",
    ],
  },
];

const PLATFORM_FEATURES = [
  { icon: CreditCard, title: "NFC cards & wearables", desc: "Metal, black or custom — one tap shares the entire profile." },
  { icon: Layers, title: "Multiple cards per user", desc: "Sales, recruiting or event-specific identities." },
  { icon: FileSignature, title: "Contracts & e-signature", desc: "Send and sign agreements without leaving the contact." },
  { icon: Workflow, title: "Pipelines & tasks", desc: "Move deals forward with shared workflows." },
  { icon: Zap, title: "Automations", desc: "Triggers across CRM, email and Slack." },
  { icon: BarChart3, title: "Analytics & exports", desc: "Real-time dashboards, BigQuery & Sheets." },
  { icon: Database, title: "Open API & webhooks", desc: "Plug banana into your existing stack." },
  { icon: Globe, title: "Multi-region hosting", desc: "EU & US data residency available." },
];

const SECURITY = [
  { icon: KeyRound, title: "SAML SSO", desc: "Okta, Google, Azure AD and any SAML 2.0 IdP." },
  { icon: UserPlus, title: "SCIM provisioning", desc: "Automatic user & group sync." },
  { icon: Lock, title: "Role-based access", desc: "Owner, Admin, Manager, Member, Guest." },
  { icon: ShieldCheck, title: "SOC 2 Type II + GDPR", desc: "Independently audited, EU-hosted option." },
  { icon: Settings2, title: "Audit logs", desc: "Every action exported to your SIEM." },
  { icon: Building2, title: "Custom DPA & MSA", desc: "Legal-ready paperwork on enterprise plans." },
];

const USE_CASES = [
  { title: "Sales teams", desc: "Capture leads at events, auto-enrich, and route to the right rep before competitors blink.", href: "/solutions/sales" },
  { title: "Agencies & consultancies", desc: "Give every consultant a polished identity and a shared book of business.", href: "/solutions/founders" },
  { title: "Real estate teams", desc: "Stay top of mind with every buyer, seller and vendor across all listings.", href: "/solutions/real-estate" },
  { title: "Enterprise", desc: "SSO, SCIM, audit logs and seat-based billing for orgs of 500+.", href: "/solutions/enterprise" },
];

const STEPS = [
  { title: "Book a demo", desc: "30 minutes with a specialist to map your rollout." },
  { title: "Invite & brand", desc: "We onboard your team and lock the brand template." },
  { title: "Ship NFC cards", desc: "Bulk fulfilment direct to desks or offices, worldwide." },
  { title: "Measure & scale", desc: "Track leads & revenue influenced from day one." },
];

const FAQ = [
  { q: "How is Teams different from the free plan?", a: "Teams adds a shared workspace, brand-locked templates, bulk invite, SSO, role-based access, team analytics and priority support. Enterprise adds SCIM, audit logs, custom DPA and a dedicated CSM." },
  { q: "Can we keep our existing CRM?", a: "Yes. Banana plugs into Salesforce, HubSpot, Pipedrive, Attio and others via native integrations or webhooks. Use banana as the capture layer, your CRM as the system of record — or run banana standalone." },
  { q: "Do you ship NFC cards globally?", a: "Yes. We fulfil from EU and US hubs to 100+ countries, with re-orders managed centrally from your team dashboard." },
  { q: "What happens when someone leaves the team?", a: "One click off-boards the seat: the card stops sharing, contacts stay in the workspace, and ownership reassigns to a teammate. No data lost." },
  { q: "Is banana GDPR & SOC 2 compliant?", a: "Yes — SOC 2 Type II audited, GDPR-compliant, with an EU-only data residency option on enterprise plans." },
];

function TeamsPage() {
  return (
    <SiteLayout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-background">
        <div className="pointer-events-none absolute inset-0 -z-10 bg-grid mask-fade-edges" aria-hidden />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-14 pt-16 md:grid-cols-12 md:px-6 md:pt-24">
          <div className="md:col-span-7">
            <p className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
              banana.cards for Teams
            </p>
            <h1 className="mt-6 max-w-3xl font-display leading-[1.02] tracking-[-0.02em] md:text-[5.25rem] md:leading-[0.98] font-bold text-5xl">
              One platform. Every employee.{" "}
              <em className="italic">Every lead captured.</em>
            </h1>
            <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
              Roll out branded digital cards, NFC products and an AI CRM across the whole team —
              with the SSO, analytics and admin controls IT actually approves of.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-dark px-6 py-3 text-sm font-bold text-background transition hover:opacity-90"
              >
                Book a demo
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/80"
              >
                See team pricing
              </Link>
            </div>
            <dl className="mt-12 grid max-w-2xl grid-cols-2 gap-x-8 gap-y-6 border-t border-foreground/10 pt-8 md:grid-cols-4">
              {HERO_METRICS.map((m) => (
                <div key={m.l}>
                  <dt className="font-display text-3xl leading-none md:text-4xl">{m.v}</dt>
                  <dd className="mt-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {m.l}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="relative md:col-span-5">
            <div className="absolute -inset-8 -z-10 rounded-[2.5rem] bg-foreground/5 blur-3xl" aria-hidden />
            <div className="relative overflow-hidden rounded-[2rem] bg-muted">
              <img src={networking} alt="A team using banana.cards" loading="eager" width={1600} height={2000} className="aspect-[4/5] w-full object-cover" />
            </div>
            <div className="absolute -left-6 top-10 hidden rounded-2xl bg-background p-3 md:flex md:items-center md:gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-lime">
                <Users className="h-4 w-4" />
              </span>
              <div className="pr-1">
                <p className="text-[11px] font-bold leading-none">42 seats provisioned</p>
                <p className="mt-1 text-[10.5px] text-muted-foreground">via Okta SCIM</p>
              </div>
            </div>
            <div className="absolute -right-4 bottom-6 hidden rounded-2xl bg-background p-3 md:block">
              <p className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">Q3 leads captured</p>
              <p className="mt-1 font-display text-2xl leading-none">3,184</p>
            </div>
          </div>
        </div>

        {/* trusted-by strip */}
        <div className="bg-muted py-7">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <p className="text-center text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
              Trusted by sales teams, agencies and enterprises in 100+ countries
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
              {TRUSTED_BY.map((n) => (
                <span key={n} className="text-lg font-bold tracking-tight text-foreground/40">{n}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mb-12 max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Why teams choose banana</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
            The networking stack your team{" "}
            <em className="italic">actually adopts.</em>
          </h2>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="rounded-2xl bg-muted p-7">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <h3 className="mt-6 text-xl font-bold leading-snug">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                <ul className="mt-5 grid gap-2">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
                      <span className="font-medium">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        </div>
      </section>

      {/* DEEP DIVES */}
      {DEEP_DIVES.map((d, i) => (
        <section key={d.title} className={i % 2 === 0 ? "bg-muted" : "bg-background"}>
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 md:grid-cols-2 md:px-6">
            <div className={`relative ${d.reverse ? "md:order-2" : ""}`}>
              <div className="absolute -inset-6 rounded-[2rem] bg-foreground/5 blur-3xl" aria-hidden />
              <img src={d.image} alt={d.title} loading="lazy" width={1600} height={1024} className="relative w-full rounded-2xl object-cover" />
            </div>
            <div className={d.reverse ? "md:order-1" : ""}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{d.eyebrow}</p>
              <h2 className="mt-3 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                {d.title} <em className="italic">{d.italic}</em>
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">{d.desc}</p>
              <ul className="mt-6 grid gap-2.5">
                {d.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2.5 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" /> <span className="font-medium">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ))}

      {/* PLATFORM FEATURES */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mb-12 flex flex-col items-start gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Everything included</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              One license. <em className="italic">The whole platform.</em>
            </h2>
          </div>
          <Link to="/pricing" className="inline-flex items-center gap-1 text-sm font-bold underline-offset-4 hover:underline">
            Compare plans <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {PLATFORM_FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="rounded-2xl bg-muted p-5">
                <Icon className="h-5 w-5" strokeWidth={1.75} />
                <p className="mt-4 text-sm font-bold">{f.title}</p>
                <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{f.desc}</p>
              </div>
            );
          })}
        </div>
        </div>
      </section>

      {/* SECURITY */}
      <section className="bg-brand-dark text-background">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 md:grid-cols-12 md:px-6">
          <div className="md:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-lime">Security & compliance</p>
            <h2 className="mt-3 font-display text-3xl leading-[1.1] md:text-5xl">
              Built for IT. <em className="italic">Loved by legal.</em>
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-background/70">
              banana ships with the controls enterprise buyers expect — and the paperwork to back it up.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground"
            >
              Talk to security <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-3 md:col-span-7 sm:grid-cols-2">
            {SECURITY.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="rounded-2xl bg-primary-foreground/[0.06] p-5 backdrop-blur">
                  <Icon className="h-5 w-5 text-brand-lime" strokeWidth={1.75} />
                  <p className="mt-4 text-sm font-bold">{s.title}</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-background/65">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Built for every team</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            However your team meets people, <em className="italic">we've got you.</em>
          </h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {USE_CASES.map((u) => (
            <Link
              key={u.href}
              to={u.href}
              aria-label={`${u.title} — learn more about this use case`}
              className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70"
            >
              <p className="text-base font-bold">{u.title}</p>
              <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{u.desc}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-xs font-bold underline-offset-4 group-hover:underline">
                Learn more <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          ))}
        </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="bg-muted">
        <div className="mx-auto max-w-5xl px-4 py-24 text-center md:px-6">
          <Sparkles className="mx-auto h-6 w-6" />
          <p className="mx-auto mt-6 max-w-3xl font-display text-3xl leading-tight md:text-5xl">
            "We rolled out banana to 120 reps in a week. Lead capture at conferences is{" "}
            <em className="italic">65× faster</em> — and our pipeline has never been cleaner."
          </p>
          <p className="mt-8 text-sm font-bold">Priya Sharma</p>
          <p className="text-xs text-muted-foreground">VP Sales, Northbeam</p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Rollout in 4 steps</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Live in a week, <em className="italic">not a quarter.</em>
          </h2>
        </div>
        <ol className="mt-12 grid gap-4 md:grid-cols-4">
          {STEPS.map((s, i) => (
            <li key={s.title} className="relative rounded-2xl bg-muted p-6">
              <span className="absolute -top-3 left-6 inline-flex h-7 items-center justify-center rounded-full bg-foreground px-3 text-[11px] font-bold text-background">
                Step {i + 1}
              </span>
              <h3 className="mt-3 text-base font-bold">{s.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </li>
          ))}
        </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted">
        <div className="mx-auto max-w-4xl px-4 py-24 md:px-6">
        <div className="mb-10 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">FAQ</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Questions, <em className="italic">answered.</em>
          </h2>
        </div>
        <div className="divide-y divide-foreground/10 rounded-2xl bg-card">
          {FAQ.map((f) => (
            <details key={f.q} className="group p-5">
              <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-bold">
                {f.q}
                <ArrowRight className="h-4 w-4 shrink-0 transition group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
        </div>
      </section>

      {/* OUTRO */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="relative overflow-hidden rounded-3xl bg-brand-dark px-8 py-16 text-center text-background md:p-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.12),transparent_55%)]" aria-hidden />
          <Headphones className="mx-auto h-6 w-6 text-brand-lime" />
          <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
            Ready to roll out banana to your team{" "}
            <em className="italic text-brand-lime">this week?</em>
          </h2>
          <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
            30-minute demo with a specialist. Tailored pricing for 5 to 5,000 seats. No pressure.
          </p>
          <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
              Book a demo
            </Link>
            <Link to="/pricing" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
              See team pricing
            </Link>
          </div>
        </div>
        </div>
      </section>
    </SiteLayout>
  );
}